/**
 * Shared mockup composite — extracted from dialog/mockup-preview.js so both
 * the dialog Result-slot tile row AND the F4 cached-template handler can
 * use one source of truth for template loading + canvas composition.
 *
 * Templates are read from `/templates/mockups/index.json`. Two renderers:
 *   - 'png' — full PNG composite (background + print_area overlay + optional alpha mask)
 *   - 'svg' — synthetic SVG silhouette (zero binary assets, fallback for v1)
 *
 * `print_area` may be either pixel coords (PNG renderer) or 0-1 fractional
 * coords (SVG renderer; works at any tile size).
 */

const TILE_SIZE = 256

let _manifestCache = null

// ---- Manifest ----

export async function loadManifest() {
  if (_manifestCache) return _manifestCache
  try {
    const res = await fetch('/templates/mockups/index.json', { cache: 'force-cache' })
    if (!res.ok) throw new Error(`manifest ${res.status}`)
    const m = await res.json()
    _manifestCache = (m.templates || []).filter(t => t.active !== false)
  } catch (err) {
    console.warn('[mockup] manifest load failed:', err.message)
    _manifestCache = []
  }
  return _manifestCache
}

export function clearManifestCache() {
  _manifestCache = null
}

// ---- Composite ----

export async function composeOne(template, resultImageUrl, size = TILE_SIZE) {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')

  if (template.renderer === 'png') {
    await composePngTemplate(ctx, template, resultImageUrl, size)
  } else {
    composeSvgTemplate(ctx, template, size)
    await composePrintArea(ctx, template, resultImageUrl, size)
  }

  return { id: template.id, dataUrl: canvas.toDataURL('image/png'), template }
}

async function composePngTemplate(ctx, template, resultImageUrl, size) {
  const [bg, fg] = await Promise.all([
    loadImage(`/templates/mockups/${template.id}.png`),
    loadImage(resultImageUrl),
  ])
  ctx.drawImage(bg, 0, 0, size, size)
  const pa = resolvePrintArea(template, size)
  ctx.drawImage(fg, pa.x, pa.y, pa.width, pa.height)
}

function composeSvgTemplate(ctx, template, size) {
  const bg = backgroundForColor(template.color)
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, size, size)
  drawSilhouette(ctx, template.product_type, size, isDarkColor(template.color))
}

// Dark garments (tshirt-black, H-2026-09-12-127) need a light silhouette or
// the outline vanishes into the backdrop. Image-content pixels, not UI chrome.
function isDarkColor(color) {
  return (color || '').toLowerCase() === 'black'
}

async function composePrintArea(ctx, template, resultImageUrl, size) {
  const fg = await loadImage(resultImageUrl).catch(() => null)
  if (!fg) return
  const pa = resolvePrintArea(template, size)
  ctx.drawImage(fg, pa.x, pa.y, pa.width, pa.height)
}

function resolvePrintArea(template, size) {
  const { x, y, width, height } = template.print_area || { x: 0.2, y: 0.2, width: 0.6, height: 0.6 }
  const isFrac = x <= 1 && y <= 1 && width <= 1 && height <= 1
  return isFrac
    ? { x: x * size, y: y * size, width: width * size, height: height * size }
    : { x, y, width, height }
}

// Image-content colors, not themed UI chrome — the composite backdrop and the
// silhouette stroke/fill below are drawn into the mockup image pixels and must
// not follow the theme (a "white shirt" tile stays white in any app theme).
function backgroundForColor(color) {
  switch ((color || '').toLowerCase()) {
    case 'white':   return '#f7f7f7'
    case 'natural': return '#e8e0d4'
    case 'clear':   return '#dfe7ea'
    case 'black':   return '#222'
    default:        return '#eaeaea'
  }
}

function drawSilhouette(ctx, productType, size, dark = false) {
  ctx.save()
  ctx.strokeStyle = dark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.10)'
  ctx.lineWidth = 1
  ctx.fillStyle = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'

  const cx = size / 2
  const cy = size / 2

  switch ((productType || '').toLowerCase()) {
    case 't-shirt': {
      ctx.beginPath()
      ctx.moveTo(cx - size * 0.30, cy - size * 0.30)
      ctx.lineTo(cx - size * 0.40, cy - size * 0.18)
      ctx.lineTo(cx - size * 0.32, cy + size * 0.40)
      ctx.lineTo(cx + size * 0.32, cy + size * 0.40)
      ctx.lineTo(cx + size * 0.40, cy - size * 0.18)
      ctx.lineTo(cx + size * 0.30, cy - size * 0.30)
      ctx.quadraticCurveTo(cx, cy - size * 0.20, cx - size * 0.30, cy - size * 0.30)
      ctx.closePath()
      ctx.fill()
      ctx.stroke()
      break
    }
    case 'mug': {
      ctx.beginPath()
      ctx.rect(cx - size * 0.28, cy - size * 0.22, size * 0.50, size * 0.48)
      ctx.fill()
      ctx.stroke()
      ctx.beginPath()
      ctx.ellipse(cx + size * 0.30, cy, size * 0.10, size * 0.18, 0, -Math.PI / 2, Math.PI / 2)
      ctx.stroke()
      break
    }
    case 'poster': {
      ctx.beginPath()
      ctx.rect(cx - size * 0.42, cy - size * 0.42, size * 0.84, size * 0.84)
      ctx.fill()
      ctx.stroke()
      break
    }
    case 'tote bag': {
      ctx.beginPath()
      ctx.rect(cx - size * 0.30, cy - size * 0.20, size * 0.60, size * 0.55)
      ctx.fill()
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(cx - size * 0.18, cy - size * 0.20, size * 0.10, Math.PI, 0)
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(cx + size * 0.18, cy - size * 0.20, size * 0.10, Math.PI, 0)
      ctx.stroke()
      break
    }
    case 'phone case': {
      const r = size * 0.04
      const w = size * 0.36
      const h = size * 0.66
      const x0 = cx - w / 2
      const y0 = cy - h / 2
      ctx.beginPath()
      ctx.moveTo(x0 + r, y0)
      ctx.lineTo(x0 + w - r, y0)
      ctx.quadraticCurveTo(x0 + w, y0, x0 + w, y0 + r)
      ctx.lineTo(x0 + w, y0 + h - r)
      ctx.quadraticCurveTo(x0 + w, y0 + h, x0 + w - r, y0 + h)
      ctx.lineTo(x0 + r, y0 + h)
      ctx.quadraticCurveTo(x0, y0 + h, x0, y0 + h - r)
      ctx.lineTo(x0, y0 + r)
      ctx.quadraticCurveTo(x0, y0, x0 + r, y0)
      ctx.closePath()
      ctx.fill()
      ctx.stroke()
      break
    }
    default:
      break
  }
  ctx.restore()
}

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Image load failed: ${src}`))
    img.src = src
  })
}

export const MOCKUP_TILE_SIZE = TILE_SIZE
